<template>
  <div class="campaign-bid-input">
    <div class="campaign-bid-input___symbols campaign-bid-input___dollar-sign">
      <font-awesome-icon
        class="campaign-bid-input___symbols___dollar_icon"
        :icon="['fas', 'dollar-sign']"
      />
    </div>
    <b-form-group class="width-95 my-0 py-0 campaign-bid-input___current-value">
      <b-form-input
        v-model="currentValue"
        max="9999.99"
        type="number"
        min="0.01"
        step="0.01"
        :formatter="formatter"
        class="custom-form-input--sm campaign-bid-input___current-value___input"
        @input="updateBidOnInput"
      />
    </b-form-group>
    <div
      v-long-press="500"
      style="cursor: pointer;"
      class="campaign-bid-input___symbols campaign-bid-input___increase-bid"
      @pointerdown="ignoreNextClick = false"
      @long-press-start="onLongPressStartIncrement"
      @long-press-stop="onLongPressStop"
      @click.prevent="stepBid(BID_STEP_IN_USD)"
    >
      <font-awesome-icon
        class="campaign-bid-input___symbols___change_icon"
        :icon="['fas', 'angle-up']"
      />
    </div>
    <div
      v-long-press="500"
      style="cursor: pointer;"
      class="campaign-bid-input___symbols campaign-bid-input___decrease-bid"
      @pointerdown="ignoreNextClick = false"
      @long-press-start="onLongPressStartDecrement"
      @long-press-stop="onLongPressStop"
      @click.prevent="stepBid(-BID_STEP_IN_USD)"
    >
      <font-awesome-icon
        class="campaign-bid-input___symbols___change_icon"
        :icon="['fas', 'angle-down']"
      />
    </div>
  </div>
</template>

<script>

import { mapActions } from 'vuex'
import { showErrorMessage } from '@/notification-utils'
import { getMinBidMessage } from '@/utils/campaignMinBid'
import LongPress from 'vue-directive-long-press'

// What one arrow click, or one tick of a long press, moves the bid by. The field itself still
// takes any cent value, and a step keeps the cents it starts from: $45.53 steps to $45.58
const BID_STEP_IN_USD = 0.05
// The highest bid updateBid writes is just under this
const BID_CEILING_IN_USD = 9999.99

export default {
  name: 'CampaignBidInput',
  directives: {
    'long-press': LongPress
  },
  props: {
    campaignId: {
      type: String,
      default: null
    },
    initialValue: {
      type: Number,
      default: 0
    },
    // The campaign's category minimum bid. A static bid below it disables the campaign's target
    // in Ringba, so it is never written; 0 means no minimum
    minBid: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      disabled: false,
      currentValue: 0,
      interval: null,
      inputDebounceTimeout: null,
      pendingDebouncedBidValue: null,
      longPressActive: false,
      ignoreNextClick: false,
      savesInFlight: 0,
      // The bid of the latest write sent, which the row has not caught up with while it is in flight
      lastSentBid: null,
      BID_STEP_IN_USD
    }
  },
  computed: {
    // The lowest bid an arrow steps to: updateBid's floor, or the category minimum above it
    bidFloor() {
      return Math.max(0.01, this.minBid)
    }
  },
  watch: {
    // The saved bid moved under this input: a confirmed raise, a refetch, or this input's own
    // save landing. Follow it only while the buyer is not mid-edit - a typed bid still waiting
    // on the debounce, a long press or a save still in flight is newer than what the row says.
    initialValue(value) {
      if (this.inputDebounceTimeout || this.longPressActive || this.savesInFlight) return

      this.currentValue = value
    }
  },
  created() {
    this.currentValue = this.initialValue
  },
  beforeDestroy() {
    clearInterval(this.interval)

    // Flush rather than drop: without this, unmounting within the debounce window
    // (e.g. navigating away right after typing) would silently discard the last-typed
    // bid the user saw rendered, instead of writing it
    if (this.inputDebounceTimeout) {
      clearTimeout(this.inputDebounceTimeout)
      if (this.isBelowMinBid(this.pendingDebouncedBidValue)) {
        showErrorMessage(getMinBidMessage(this.minBid))
        return
      }
      this.updateCampaignBid({ campaignId: this.campaignId, bidValue: this.pendingDebouncedBidValue })
        .catch(() => showErrorMessage('Failed to update campaign bid'))
    }
  },
  methods: {
    ...mapActions('campaign', ['updateCampaignBid']),
    updateBidOnInput(value) {
      value = Number(value)
      this.currentValue = value

      // Debounced: this fires a real write per keystroke otherwise, e.g. clearing the
      // field and typing "45" would write bid=4 then bid=45
      this.cancelPendingInputBid()
      if (!(value >= 0.01)) return

      this.pendingDebouncedBidValue = value
      this.inputDebounceTimeout = setTimeout(() => {
        this.inputDebounceTimeout = null
        this.pendingDebouncedBidValue = null
        this.saveBid(value)
      }, 1000)
    },
    // Clears the timer AND the value it would have written. beforeDestroy flushes whenever the
    // timer id is set, so a cancel that leaves it behind sends the old value on unmount:
    // backspacing "10" to empty passes through "1", and leaving the page would then save $1.
    cancelPendingInputBid() {
      clearTimeout(this.inputDebounceTimeout)
      this.inputDebounceTimeout = null
      this.pendingDebouncedBidValue = null
    },
    isBelowMinBid(value) {
      return this.minBid > 0 && value < this.minBid
    },
    async saveBid(value) {
      if (this.isBelowMinBid(value)) {
        showErrorMessage(getMinBidMessage(this.minBid))

        // Back to the bid last sent, or the saved one, unless the buyer is still typing or pressing
        if (!this.inputDebounceTimeout && !this.longPressActive) {
          this.currentValue = this.savesInFlight ? this.lastSentBid : this.initialValue
        }
        return
      }

      this.lastSentBid = value
      this.savesInFlight++
      // The bid the row holds as the save starts: by the time `saved` fires the store has already
      // written the new one into it. The campaigns list's usage event reports both
      const previousValue = this.initialValue
      try {
        await this.updateCampaignBid({ campaignId: this.campaignId, bidValue: value })
        this.$emit('saved', value, previousValue)
      } catch (error) {
        showErrorMessage('Failed to update campaign bid')

        // Back to the bid that is actually saved, unless the buyer has already moved on to a
        // newer one that is still being typed, pressed or written
        if (this.savesInFlight === 1 && !this.inputDebounceTimeout && !this.longPressActive) {
          this.currentValue = this.initialValue
        }
      } finally {
        this.savesInFlight--
      }
    },
    formatter(value) {
      const oldValue = this.currentValue
      if (value.indexOf('.') >= 0) {
        let [wholeNumber, decimal] = value.split('.')
        decimal = decimal.slice(0, 2)
        return Number(value) > 9999.99 ? oldValue : `${wholeNumber}.${decimal}`
      } else {
        return Number(value) > 9999.99 ? oldValue : value
      }
    },
    async updateBid(value) {
      // Cancel any pending debounced write from the text input, otherwise it can
      // fire after this one and clobber it with a stale value
      this.cancelPendingInputBid()

      value = Number(value.toFixed(2))
      if (value < 0.01 || value >= BID_CEILING_IN_USD) return
      this.currentValue = value
      await this.saveBid(value)
    },
    stepBid(delta) {
      // The release that ends a long press also lands as a click on the same arrow, which would
      // step the bid once more and save it a second time
      if (this.ignoreNextClick) {
        this.ignoreNextClick = false
        return
      }

      let value = Number((this.currentValue + delta).toFixed(2))

      // A step down that would cross the floor lands on it: $35.03 with a $35 minimum steps to
      // $35.00 rather than being refused. From the floor itself it is still refused, as before
      if (delta < 0 && this.currentValue > this.bidFloor && value < this.bidFloor) {
        value = this.bidFloor
      }

      this.updateBid(value)
    },
    onLongPressStartIncrement () {
      this.longPressActive = true
      this.interval = setInterval(() => {
        const value = Number((this.currentValue + BID_STEP_IN_USD).toFixed(2))
        // Stops short of what updateBid refuses, so a long press never shows a bid it then drops
        if (value >= BID_CEILING_IN_USD) return
        this.currentValue = value
      }, 100)
    },
    onLongPressStartDecrement () {
      this.longPressActive = true
      this.interval = setInterval(() => {
        // The floor updateBid and the category minimum hold, so a long press never shows a bid it
        // then refuses to save. The last step lands on the floor rather than stopping short of it
        if (this.currentValue <= this.bidFloor) return
        this.currentValue = Math.max(this.bidFloor, Number((this.currentValue - BID_STEP_IN_USD).toFixed(2)))
      }, 100)
    },
    async onLongPressStop () {
      clearInterval(this.interval)

      // vue-directive-long-press fires this on EVERY release, a plain click included, and the
      // click handler already saves those. Saving here too sent two racing writes per click.
      if (!this.longPressActive) return

      this.longPressActive = false
      this.ignoreNextClick = true
      await this.updateBid(this.currentValue)
    }
  }
}
</script>

<style lang="scss" scoped>
// Inline, so the cell's text-align places it: a block-level flex row fills the cell and sits at
// its left edge, even in the campaigns table's right-aligned Bid column
.campaign-bid-input {
  display: inline-flex;
}

// Centred by flex rather than by margins: inline icons follow the cell's text-align, which put
// them against the right border of their boxes in a right-aligned column
.campaign-bid-input___symbols {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 33px;
  border: 1px solid var(--component-custom-input-border-color);
  color: white;
}

// Each seam is drawn once, so it is as thin as the outer edge rather than two borders side by
// side. The input keeps all four of its own, which its focus colour needs
.campaign-bid-input___dollar-sign {
  border-right: 0;
  border-top-left-radius: 0.2rem;
  border-bottom-left-radius: 0.2rem;
}

.campaign-bid-input___increase-bid,
.campaign-bid-input___decrease-bid {
  border-left: 0;
}

.campaign-bid-input___decrease-bid {
  border-top-right-radius: 0.2rem;
  border-bottom-right-radius: 0.2rem;
}

.campaign-bid-input___symbols___change_icon {
  &:hover {
    outline: none;
    border-color: $login-font;
    cursor: pointer;
  }
}



.width-95 {
  width: 95px;
}

.campaign-bid-input___current-value {
  height: 33px;
}

.campaign-bid-input___current-value___input {
  height: 33px;
  border-radius: unset;
}


</style>
